import { useRef, useState } from "react";
import { Download, Paperclip } from "lucide-react";
import { API_URL, apiFetch } from "../lib/api-client";

export type PurchaseOrderAttachment = {
  id: string;
  fileName: string | null;
  contentUrl: string | null;
  contentType?: string | null;
  createdAt?: string;
};

export function PurchaseOrderAttachments({ order, canWrite, disabled = false, onSaved }: {
  order: { id: string; status: string; sourceImported?: number; attachments?: PurchaseOrderAttachment[] };
  canWrite: boolean;
  disabled?: boolean;
  onSaved: () => void | Promise<void>;
}) {
  const picker = useRef<HTMLInputElement>(null);
  const lock = useRef(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [uploaded, setUploaded] = useState<PurchaseOrderAttachment[]>([]);
  const attachments = [...(order.attachments ?? []), ...uploaded.filter(file => !order.attachments?.some(saved => saved.id === file.id))];
  const canAttach = canWrite;

  async function attach(file: File | undefined) {
    if (!file || !canAttach || disabled || lock.current) return;
    setError(""); setNotice("");
    if (!file.size || file.size > 25 * 1024 * 1024) {
      setError("Choose a non-empty file of 25 MB or less.");
      return;
    }
    lock.current = true; setUploading(true);
    try {
      const body = new FormData(); body.set("file", file);
      const response = await apiFetch(`/api/v1/workspace/inventory/purchase-orders/${encodeURIComponent(order.id)}/attachments`, { method: "POST", body });
      const payload = await response.json() as { data?: PurchaseOrderAttachment; error?: { message?: string } };
      if (!response.ok || !payload.data?.id) throw new Error(payload.error?.message || "The file could not be attached. Please try again.");
      setUploaded(current => [...current, payload.data!]);
      setNotice("File attached.");
      try { await onSaved(); } catch { setNotice("File attached. Reload the order to refresh its details."); }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "The file could not be attached. Please try again.");
    } finally { lock.current = false; setUploading(false); }
  }

  return <section aria-label="Purchase order attachments" className="rounded-xl border border-[var(--line)] p-4">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <h3 className="flex items-center gap-2 font-semibold"><Paperclip size={16} aria-hidden="true" />Attachments{attachments.length ? ` (${attachments.length})` : ""}</h3>
      {canWrite && <>
        <button type="button" disabled={!canAttach || disabled || uploading} onClick={() => picker.current?.click()} className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-[var(--line)] px-3 py-2 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-50">
          <Paperclip size={16} aria-hidden="true" />{uploading ? "Attaching…" : "Attach file"}
        </button>
        <input ref={picker} aria-label="Attach purchase order file" type="file" className="sr-only" tabIndex={-1} disabled={!canAttach || disabled || uploading} onChange={event => { const file = event.currentTarget.files?.[0]; event.currentTarget.value = ""; void attach(file); }} />
      </>}
    </div>
    <p className="mt-2 text-xs text-[var(--ink-soft)]">{order.sourceImported ? "Imported purchase order attachments are read only." : canAttach ? "Add quotes, receipts, or supporting documents at any time. Up to 25 MB per file." : "Files saved with this purchase order."}</p>
    {attachments.length ? <ul className="mt-3 divide-y divide-[var(--line)]">{attachments.map(file => <li key={file.id} className="py-2 text-sm">
      {file.contentUrl ? <a href={`${API_URL}${file.contentUrl}`} target="_blank" rel="noopener noreferrer" className="flex min-h-11 items-center justify-between gap-3 rounded-md px-1 font-semibold text-[var(--accent)]"><span className="min-w-0 break-words">{file.fileName || "Attachment"}</span><Download size={16} className="shrink-0" aria-hidden="true" /></a> : <span className="text-[var(--ink-soft)]">{file.fileName || "Attachment"} · File unavailable</span>}
    </li>)}</ul> : <p className="mt-3 text-sm text-[var(--ink-soft)]">No files attached.</p>}
    {error && <p role="alert" className="mt-3 text-sm text-[var(--accent)]">{error}</p>}
    {notice && <p role="status" className="mt-3 text-sm text-[var(--ink-soft)]">{notice}</p>}
  </section>;
}
